import { supabase } from '../lib/supabase';

const MEMORY_IMAGE_MAX_EDGE = 2560;
const MEMORY_IMAGE_QUALITY = 0.84;
const MEMORY_IMAGE_OPTIMIZE_FROM_BYTES = 1_500_000;
const MEMORY_IMAGE_TARGET_MAX_BYTES = 8_000_000;

export function evidenceObjectPath(projectId: number, authUserId: string, filename: string) {
  const safeName = filename.normalize('NFKD').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/\.{2,}/g, '').replace(/-+/g, '-').replace(/^-+|-+$/g, '') || 'file';
  return `${projectId}/${authUserId}/${crypto.randomUUID()}/${safeName}`;
}

export function shouldOptimizeMemoryImage(file: Pick<File, 'type' | 'size'>) {
  return file.type.startsWith('image/') && file.size >= MEMORY_IMAGE_OPTIMIZE_FROM_BYTES;
}

function targetImageSize(width: number, height: number, maxEdge = MEMORY_IMAGE_MAX_EDGE) {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

async function imageDimensionsAndDraw(file: File, canvas: HTMLCanvasElement, maxEdge: number) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      try {
        const target = targetImageSize(bitmap.width, bitmap.height, maxEdge);
        canvas.width = target.width;
        canvas.height = target.height;
        const context = canvas.getContext('2d');
        if (!context) return false;
        context.drawImage(bitmap, 0, 0, target.width, target.height);
        return true;
      } finally {
        bitmap.close();
      }
    } catch {
      // Fall through to the HTMLImageElement path. Some Android camera JPEGs fail createImageBitmap.
    }
  }

  if (typeof Image === 'undefined' || typeof URL === 'undefined') return false;
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image decode failed'));
      image.src = objectUrl;
    });
    const target = targetImageSize(image.naturalWidth, image.naturalHeight, maxEdge);
    canvas.width = target.width;
    canvas.height = target.height;
    const context = canvas.getContext('2d');
    if (!context) return false;
    context.drawImage(image, 0, 0, target.width, target.height);
    return true;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function canvasJpeg(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

export async function optimizeMemoryImage(file: File): Promise<File> {
  if (!shouldOptimizeMemoryImage(file) || typeof document === 'undefined') return file;

  const attempts = [
    { maxEdge: 2560, quality: MEMORY_IMAGE_QUALITY },
    { maxEdge: 2304, quality: 0.78 },
    { maxEdge: 2048, quality: 0.72 },
    { maxEdge: 1792, quality: 0.66 },
  ];
  let best: Blob | null = null;

  for (const attempt of attempts) {
    const canvas = document.createElement('canvas');
    if (!(await imageDimensionsAndDraw(file, canvas, attempt.maxEdge))) continue;
    const blob = await canvasJpeg(canvas, attempt.quality);
    if (!blob) continue;
    if (!best || blob.size < best.size) best = blob;
    if (blob.size <= MEMORY_IMAGE_TARGET_MAX_BYTES) break;
  }

  if (!best || best.size >= file.size) return file;
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'memory';
  return new File([best], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
}

export async function uploadEvidence(path: string, file: File) {
  const uploadFile = await optimizeMemoryImage(file);
  const { data, error } = await supabase.storage.from('evidence').upload(path, uploadFile, { contentType: uploadFile.type || file.type, upsert: false });
  if (error) throw error;
  return data;
}

const SIGNED_URL_TTL_SECONDS = 3600;

export async function downloadEvidence(path: string) {
  const { data, error } = await supabase.storage.from('evidence').createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return data.signedUrl;
}

/**
 * Sign a batch of storage keys for display. Evidence rows persist only the storage key; callers re-sign on read so
 * links do not expire (the previous approach stored a 60s signed URL, leaving thumbnails broken a minute after upload).
 */
export async function signedEvidenceUrls(paths: string[]): Promise<Map<string, string>> {
  const unique = [...new Set(paths.filter((p): p is string => Boolean(p)))];
  if (unique.length === 0) return new Map();
  const { data, error } = await supabase.storage.from('evidence').createSignedUrls(unique, SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return new Map((data ?? []).filter((d) => d.path && d.signedUrl).map((d) => [d.path as string, d.signedUrl as string]));
}

export async function removeEvidenceObject(path: string) {
  const { error } = await supabase.storage.from('evidence').remove([path]);
  if (error) throw error;
}
